import React, { useId } from 'react';
import { cx } from '@/ui/utils/cx';
import { typography } from '@/ui/theme/tokens';

export type AppFormFieldProps = {
  label: React.ReactNode;
  htmlFor?: string;
  required?: boolean;
  helperText?: React.ReactNode;
  error?: React.ReactNode;
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
};

function mergeFieldId(
  child: React.ReactElement,
  fieldId: string,
  describedBy: string | undefined,
  disabled: boolean,
  hasError: boolean,
) {
  return React.cloneElement(child, {
    id: (child.props as { id?: string }).id ?? fieldId,
    disabled: (child.props as { disabled?: boolean }).disabled ?? disabled,
    'aria-describedby': describedBy,
    'aria-invalid': hasError ? true : undefined,
    hasError,
  } as Record<string, unknown>);
}

export default function AppFormField({
  label,
  htmlFor,
  required = false,
  helperText,
  error,
  disabled = false,
  className,
  children,
}: AppFormFieldProps) {
  const autoId = useId();
  const fieldId = htmlFor ?? `field-${autoId.replace(/:/g, '')}`;
  const helperId = helperText ? `${fieldId}-helper` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const describedBy = [helperId, errorId].filter(Boolean).join(' ') || undefined;
  const hasError = Boolean(error);

  const control = React.isValidElement(children)
    ? mergeFieldId(children, fieldId, describedBy, disabled, hasError)
    : children;

  return (
    <div
      className={cx(
        'app-form-field',
        hasError && 'app-form-field--error',
        disabled && 'app-form-field--disabled',
        className,
      )}
    >
      <div className="app-form-field__label-row">
        <label htmlFor={fieldId} className={cx('app-form-field__label', typography.fieldLabel)}>
          {label}
        </label>
        {required ? (
          <span className="app-form-field__required" aria-hidden title="Required">
            *
          </span>
        ) : null}
      </div>
      {control}
      {helperText ? (
        <p id={helperId} className={cx('app-form-field__helper', typography.fieldHelper)}>
          {helperText}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} className="app-form-field__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
